import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCheckCircle,
  FaDollarSign,
  FaEdit,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPen,
  FaPhoneAlt,
  FaPlus,
  FaQuestionCircle,
  FaShieldAlt,
  FaUniversity,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { graphqlRequest } from '../../utils/graphqlRequest';
import {
  formatTypeOfWorkLabel,
  skillTagsFromStoredWork,
} from '../../utils/workProfile';
import '../../styles/AdminPersonalInformation.css';

const GET_MY_PROFILE = `
  query GetMyProfile {
    getMyProfile {
      id
      firstName
      lastName
      email
      contact
      DOB
      gender
      address
      taxResidency
      emergencyContactName
      emergencyContactNumber
      bsb
      accountNumber
      accountName
      taxFileNumber
      abnNumber
      claimTaxFreeThreshold
      superannuationAccountNumber
      jobTitle
      salaryTerm
      typeOfWork
      paydayDay
      joiningDate
      payType
      perHourRate
      fundName
      spin
      fundABN
      fundTelephoneNumber
      memberNumber
      roles
      upload
      isActive
    }
  }
`;

const GET_WORKFORCE_DOCUMENTS = `
  query GetWorkforceDocumentsForUser($userId: String) {
    getWorkforceDocuments(userId: $userId) {
      id
      userId
      employeeCode
      employeeName
      documentType
      cleaningLicense
      experienceYears
      fileName
      fileKey
      fileUrl
      fileType
      fileSize
      createdAt
    }
  }
`;

const GENERATE_UPLOAD_URL = `
  mutation GenerateUrlForImageUpload($files: [Fileupload!]!) {
    generateUrlForImageUploadClient(files: $files) {
      key
      url
    }
  }
`;

const GENERATE_WORKFORCE_UPLOAD_URL = `
  mutation WorkforceUpload($userId: String!, $fileName: String!, $contentType: String!) {
    createWorkforceDocumentUploadUrl(userId: $userId, fileName: $fileName, contentType: $contentType) { key url }
  }
`;

const UPDATE_MY_PROFILE_UPLOAD = `
  mutation UpdateMyProfileUpload($input: UpdateUserInput!) {
    UpdateUser(UpdateUserInput: $input) {
      id
      upload
    }
  }
`;

const CREATE_WORKFORCE_DOCUMENT = `
  mutation CreateWorkforceDocument($input: CreateWorkforceDocumentInput!) {
    createWorkforceDocument(input: $input) {
      id
    }
  }
`;

const REMOVE_WORKFORCE_DOCUMENT = `
  mutation RemoveWorkforceDocument($id: String!) {
    removeWorkforceDocument(id: $id)
  }
`;

const GET_ALL_USERS = `
  query GetAllUser {
    getAllUser {
      id
      roles
    }
  }
`;

const DOCUMENT_TYPES = ['CV', 'Academic Document', 'Cleaning License', 'Experience Certificate'];

const toDisplay = (value) => {
  const text = value === null || value === undefined ? '' : String(value).trim();
  return text || 'N/A';
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });

const normalizeRole = (role) =>
  String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[/\s-]+/g, '_');

const hasAnyRole = (roles = [], expectedRoles = []) => {
  const normalizedExpected = expectedRoles.map(normalizeRole);
  return (roles || []).some((role) => normalizedExpected.includes(normalizeRole(role)));
};

const buildEmployeeCodeMap = (users = []) => {
  const adminUsers = users.filter((u) =>
    hasAnyRole(u?.roles, ['ADMIN', 'SUPER_ADMIN', 'BRANCH_SUPER_ADMIN']),
  );
  const managerUsers = users.filter((u) => hasAnyRole(u?.roles, ['OPERATIONS_MANAGER', 'SUPERVISOR']));
  const cleanerUsers = users.filter((u) => hasAnyRole(u?.roles, ['FIELD_CLEANER']));
  const contractorUsers = users.filter((u) => hasAnyRole(u?.roles, ['SCHEDULER_DISPATCHER']));

  const mapRows = (rows, prefix) =>
    rows.map((row, index) => ({
      userId: String(row?.id || ''),
      employeeCode: `${prefix}${String(index + 1).padStart(3, '0')}`,
    }));

  const mapped = [
    ...mapRows(adminUsers, 'ADM'),
    ...mapRows(managerUsers, 'MGM'),
    ...mapRows(cleanerUsers, 'CLR'),
    ...mapRows(contractorUsers, 'CTR'),
  ];

  return mapped.reduce((acc, item) => {
    if (item.userId) acc[item.userId] = item.employeeCode;
    return acc;
  }, {});
};

const resolveUploadUrl = (rawUpload) => {
  if (!rawUpload) return '';

  // already direct URL / data URL
  if (typeof rawUpload === 'string') {
    const value = rawUpload.trim();
    if (!value) return '';
    if (value.startsWith('{') && value.endsWith('}')) {
      try {
        const parsed = JSON.parse(value);
        return resolveUploadUrl(parsed);
      } catch {
        // ignore invalid JSON and continue normal handling
      }
    }
    if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:')) return value;
    if (value.startsWith('/')) return `${window.location.origin}${value}`;

    // if only S3 key was stored (common admin flow), build URL from env when available
    const bucket = process.env.REACT_APP_AWS_BUCKET_NAME;
    const region = process.env.REACT_APP_AWS_REGION;
    if (bucket && region) {
      return `https://${bucket}.s3.${region}.amazonaws.com/${value}`;
    }
    // Do not fallback to app-origin paths for S3 keys.
    // That would load the SPA route (dashboard) inside the preview instead of the actual file.
    return '';
  }

  // support object/json variants
  if (typeof rawUpload === 'object') {
    return rawUpload.url || rawUpload.dataUrl || rawUpload.key || '';
  }

  return '';
};

export default function PersonalInformation() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [workforceDocuments, setWorkforceDocuments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoMessage, setPhotoMessage] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [documentPreview, setDocumentPreview] = useState(null);
  const [documentEditor, setDocumentEditor] = useState({
    open: false,
    mode: 'add',
    docId: null,
    fileKey: '',
    fileName: '',
    fileType: '',
    fileSize: 0,
  });
  const [docType, setDocType] = useState('');
  const [docTypeOther, setDocTypeOther] = useState('');
  const [docExperience, setDocExperience] = useState('');
  const [docCleaningLicense, setDocCleaningLicense] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [savingDocument, setSavingDocument] = useState(false);
  const [docMessage, setDocMessage] = useState('');
  const [docError, setDocError] = useState('');
  const fileInputRef = useRef(null);
  const docFileInputRef = useRef(null);

  const fetchData = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      let me = null;
      try {
        const profileRes = await graphqlRequest(GET_MY_PROFILE);
        me = profileRes?.getMyProfile || null;
      } catch (e) {
        console.error('getMyProfile failed', e);
      }
      setProfile(me);
      const uid = me?.id ? String(me.id) : '';
      if (!uid) {
        setWorkforceDocuments([]);
        setAllUsers([]);
        return;
      }
      const [docsData, usersData] = await Promise.allSettled([
        graphqlRequest(GET_WORKFORCE_DOCUMENTS, { userId: uid }),
        graphqlRequest(GET_ALL_USERS),
      ]);
      if (docsData.status === 'fulfilled') {
        setWorkforceDocuments(
          Array.isArray(docsData.value?.getWorkforceDocuments) ? docsData.value.getWorkforceDocuments : [],
        );
      } else {
        setWorkforceDocuments([]);
      }
      if (usersData.status === 'fulfilled') {
        setAllUsers(Array.isArray(usersData.value?.getAllUser) ? usersData.value.getAllUser : []);
      } else {
        setAllUsers([]);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setProfile(null);
      setWorkforceDocuments([]);
      setAllUsers([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const handleChoosePhoto = () => {
    if (uploadingPhoto) return;
    setPhotoError('');
    setPhotoMessage('');
    fileInputRef.current?.click();
  };

  const handleUploadPhoto = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setPhotoError('Please select a valid image file.');
      return;
    }

    try {
      setUploadingPhoto(true);
      setPhotoError('');
      setPhotoMessage('');
      const contentType = file.type || 'image/jpeg';

      const uploadData = await graphqlRequest(GENERATE_UPLOAD_URL, {
        files: [{ fileName: file.name, contentType }],
      });
      const uploadTarget = uploadData?.generateUrlForImageUploadClient?.[0];
      if (!uploadTarget?.url || !uploadTarget?.key) {
        throw new Error('Failed to prepare image upload.');
      }

      try {
        await axios.put(uploadTarget.url, file, {
          headers: { 'Content-Type': contentType },
        });
      } catch (uploadErr) {
        const status = uploadErr?.response?.status;
        if (status === 403) {
          // Fallback: store as data URL if S3 upload is blocked.
          const dataUrl = await readFileAsDataUrl(file);
          await graphqlRequest(UPDATE_MY_PROFILE_UPLOAD, {
            input: { upload: dataUrl },
          });
          await fetchData(false);
          setPhotoMessage('Profile photo updated successfully.');
          return;
        }
        throw uploadErr;
      }

      try {
        await graphqlRequest(UPDATE_MY_PROFILE_UPLOAD, {
          input: { upload: uploadTarget.key },
        });
      } catch (saveErr) {
        const status = saveErr?.response?.status;
        if (status === 403) {
          throw new Error('PROFILE_SAVE_403');
        }
        throw saveErr;
      }

      await fetchData(false);
      setPhotoMessage('Profile photo updated successfully.');
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      if (error?.message === 'PROFILE_SAVE_403') {
        setPhotoError('Image uploaded but profile save was rejected (403). Please login again and retry.');
      } else {
        setPhotoError(error?.message || 'Failed to upload photo. Please try again.');
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  const displayName = useMemo(() => {
    const fromProfile = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim();
    return fromProfile || profile?.email || Cookies.get('userName') || 'User';
  }, [profile?.firstName, profile?.lastName, profile?.email]);

  const roleLabel = useMemo(() => {
    if (profile?.jobTitle) return profile.jobTitle;
    try {
      const r = JSON.parse(localStorage.getItem('userRoles') || '[]');
      return Array.isArray(r) && r.length ? String(r[0]).replace(/_/g, ' ') : 'Admin';
    } catch {
      return 'Admin';
    }
  }, [profile?.jobTitle]);

  const idPrefix = 'ADM';
  const employeeCodeMap = useMemo(() => buildEmployeeCodeMap(allUsers), [allUsers]);
  const existingCode = workforceDocuments.find((doc) => doc?.employeeCode)?.employeeCode || '';
  const mappedCode = employeeCodeMap[String(profile?.id || '')] || '';
  const employeeId = useMemo(() => {
    if (mappedCode) return mappedCode;
    if (existingCode) return existingCode;
    return `${idPrefix}-${String(profile?.id || '').slice(0, 4).toUpperCase() || '2024'}-${String(profile?.id || '').slice(-4).toUpperCase() || '1547'}`;
  }, [existingCode, idPrefix, mappedCode, profile?.id]);
  const resolvedUploadUrl = resolveUploadUrl(profile?.upload);

  const maybeSkills = skillTagsFromStoredWork(profile?.typeOfWork);
  const skillTags = maybeSkills.length > 0 ? maybeSkills : ['N/A'];

  const experienceFromDocuments = useMemo(() => {
    const docs = Array.isArray(workforceDocuments) ? workforceDocuments : [];
    const withExperience = docs
      .filter((doc) => doc?.experienceYears !== null && doc?.experienceYears !== undefined)
      .map((doc) => ({
        years: Number(doc.experienceYears),
        createdAt: doc?.createdAt ? new Date(doc.createdAt).getTime() : 0,
      }))
      .filter((item) => Number.isFinite(item.years));

    if (withExperience.length === 0) return 'N/A';

    withExperience.sort((a, b) => b.createdAt - a.createdAt);
    const years = withExperience[0].years;
    return `${years} ${years === 1 ? 'Year' : 'Years'}`;
  }, [workforceDocuments]);

  const openDocumentPreview = useCallback((url, title) => {
    if (!url) return;
    const normalizedUrl = String(url).toLowerCase();
    const normalizedTitle = String(title || '').toLowerCase();
    const isImage =
      normalizedUrl.startsWith('data:image/') ||
      /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(normalizedUrl) ||
      /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(normalizedTitle);
    const isPdf =
      normalizedUrl.startsWith('data:application/pdf') ||
      /\.pdf(\?|$)/i.test(normalizedUrl) ||
      /\.pdf$/i.test(normalizedTitle);
    setDocumentPreview({
      url,
      title: title || 'Document',
      isImage,
      isPdf,
    });
  }, []);

  const closeDocumentPreview = useCallback(() => {
    setDocumentPreview(null);
  }, []);

  const resetDocumentEditor = useCallback(() => {
    setDocumentEditor({
      open: false,
      mode: 'add',
      docId: null,
      fileKey: '',
      fileName: '',
      fileType: '',
      fileSize: 0,
    });
    setDocType('');
    setDocTypeOther('');
    setDocExperience('');
    setDocCleaningLicense('');
    setDocFile(null);
  }, []);

  const openAddDocumentEditor = useCallback(() => {
    setDocError('');
    setDocMessage('');
    setDocumentEditor({
      open: true,
      mode: 'add',
      docId: null,
      fileKey: '',
      fileName: '',
      fileType: '',
      fileSize: 0,
    });
    setDocType('');
    setDocTypeOther('');
    setDocExperience(
      workforceDocuments?.[0]?.experienceYears !== null && workforceDocuments?.[0]?.experienceYears !== undefined
        ? String(workforceDocuments[0].experienceYears)
        : ''
    );
    setDocCleaningLicense(workforceDocuments?.[0]?.cleaningLicense || 'No');
    setDocFile(null);
  }, [workforceDocuments]);

  const openEditDocumentEditor = useCallback((doc) => {
    setDocError('');
    setDocMessage('');
    const type = String(doc?.documentType || '').trim();
    const isKnownType = DOCUMENT_TYPES.includes(type);
    setDocumentEditor({
      open: true,
      mode: 'edit',
      docId: doc?.id || null,
      fileKey: doc?.fileKey || '',
      fileName: doc?.fileName || '',
      fileType: doc?.fileType || '',
      fileSize: Number(doc?.fileSize || 0),
    });
    setDocType(isKnownType ? type : 'Other');
    setDocTypeOther(isKnownType ? '' : type);
    setDocExperience(
      doc?.experienceYears !== null && doc?.experienceYears !== undefined ? String(doc.experienceYears) : ''
    );
    setDocCleaningLicense(doc?.cleaningLicense || 'No');
    setDocFile(null);
  }, []);

  const handlePickDocumentFile = useCallback(() => {
    if (savingDocument) return;
    docFileInputRef.current?.click();
  }, [savingDocument]);

  const handleDocumentFileChange = useCallback((event) => {
    const file = event?.target?.files?.[0];
    event.target.value = '';
    if (!file) return;
    setDocFile(file);
  }, []);

  const saveWorkforceDocument = useCallback(async () => {
    try {
      setSavingDocument(true);
      setDocError('');
      setDocMessage('');

      const resolvedDocumentType = docType === 'Other' ? docTypeOther.trim() : docType.trim();
      if (!resolvedDocumentType) {
        setDocError('Please select document type.');
        return;
      }
      if (!docCleaningLicense) {
        setDocError('Please select cleaning license status.');
        return;
      }

      const parsedExperience = Number(docExperience);
      if (!Number.isFinite(parsedExperience) || parsedExperience < 0) {
        setDocError('Please enter valid experience years.');
        return;
      }

      if (documentEditor.mode === 'add' && !docFile) {
        setDocError('Please upload a file.');
        return;
      }

      let finalFileKey = documentEditor.fileKey;
      let finalFileName = documentEditor.fileName;
      let finalFileType = documentEditor.fileType;
      let finalFileSize = Number(documentEditor.fileSize || 0);

      if (docFile) {
        const contentType = docFile.type || 'application/octet-stream';
        const uploadData = await graphqlRequest(GENERATE_WORKFORCE_UPLOAD_URL, {
          userId: String(profile?.id || ''), fileName: docFile.name, contentType,
        });
        const uploadTarget = uploadData?.createWorkforceDocumentUploadUrl;
        if (!uploadTarget?.url || !uploadTarget?.key) {
          throw new Error('Failed to prepare document upload.');
        }

        try {
          await axios.put(uploadTarget.url, docFile, {
            headers: { 'Content-Type': contentType },
          });
          finalFileKey = uploadTarget.key;
        } catch {
          finalFileKey = await readFileAsDataUrl(docFile);
        }

        finalFileName = docFile.name;
        finalFileType = docFile.type || '';
        finalFileSize = Number(docFile.size || 0);
      }

      const existingDoc = (workforceDocuments || []).find((item) => item?.id === documentEditor.docId);
      const employeeCodeFromExisting = existingDoc?.employeeCode;
      const employeeNameFromExisting = existingDoc?.employeeName;
      const fallbackCode = employeeId || `EMP-${String(profile?.id || '').slice(-4).toUpperCase() || '0000'}`;

      const created = await graphqlRequest(CREATE_WORKFORCE_DOCUMENT, {
        input: {
          userId: String(profile?.id || ''),
          employeeCode: employeeCodeFromExisting?.startsWith('EMP-') ? fallbackCode : (employeeCodeFromExisting || fallbackCode),
          employeeName: employeeNameFromExisting || displayName,
          documentType: resolvedDocumentType,
          cleaningLicense: docCleaningLicense,
          experienceYears: parsedExperience,
          fileName: finalFileName || 'document',
          fileType: finalFileType || '',
          fileSize: finalFileSize,
          fileKey: finalFileKey,
        },
      });

      if (!created?.createWorkforceDocument?.id) {
        throw new Error('Failed to save document.');
      }

      if (documentEditor.mode === 'edit' && documentEditor.docId) {
        try {
          await graphqlRequest(REMOVE_WORKFORCE_DOCUMENT, { id: documentEditor.docId });
        } catch (removeError) {
          console.error('Failed to remove old document after edit:', removeError);
        }
      }

      await fetchData(false);
      setDocMessage(documentEditor.mode === 'edit' ? 'Document updated successfully.' : 'Document uploaded successfully.');
      resetDocumentEditor();
    } catch (error) {
      console.error('Failed to save workforce document:', error);
      setDocError(error?.message || 'Failed to save document. Please try again.');
    } finally {
      setSavingDocument(false);
    }
  }, [
    docCleaningLicense,
    docExperience,
    docFile,
    docType,
    docTypeOther,
    displayName,
    documentEditor.docId,
    documentEditor.fileKey,
    documentEditor.fileName,
    documentEditor.fileSize,
    documentEditor.fileType,
    documentEditor.mode,
    fetchData,
    resetDocumentEditor,
    employeeId,
    profile?.id,
    workforceDocuments,
  ]);

  const baseInfo = [
    { label: 'Full Name', value: displayName },
    { label: 'Phone Number', value: toDisplay(profile?.contact) },
    { label: 'Email Address', value: toDisplay(profile?.email) },
    { label: 'Residential Address', value: toDisplay(profile?.address) },
    { label: 'Date of Birth', value: toDisplay(profile?.DOB) },
    { label: 'Gender', value: toDisplay(profile?.gender) },
  ];

  const emergencyInfo = [
    { label: 'Contact Name', value: toDisplay(profile?.emergencyContactName) },
    { label: 'Relationship', value: 'N/A' },
    { label: 'Emergency Phone', value: toDisplay(profile?.emergencyContactNumber) },
  ];

  const professionalInfo = [
    { label: 'Government ID / License Number', value: toDisplay(profile?.taxFileNumber) },
    { label: 'Years of Experience', value: experienceFromDocuments },
    { label: 'Type of Work', value: formatTypeOfWorkLabel(profile?.typeOfWork) || 'N/A' },
  ];

  return (
    <div className="ctr-profile-page">
      <div className="ctr-pi-topbar">
        <button type="button" className="ctr-pi-icon-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>
        <h1>Personal Information</h1>
        <button
          type="button"
          className="ctr-pi-icon-btn"
          onClick={handleChoosePhoto}
          disabled={uploadingPhoto}
          title="Upload profile photo"
        >
          <FaPen />
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="ctr-pi-hidden-file-input"
        onChange={handleUploadPhoto}
      />

      <div className="ctr-pi-summary-card">
        <div className="ctr-pi-avatar-wrap">
          <div className="ctr-pi-avatar">
            {resolvedUploadUrl ? (
              <img src={resolvedUploadUrl} alt={displayName} className="ctr-pi-avatar-image" />
            ) : (
              '👤'
            )}
          </div>
        </div>
        <div className="ctr-pi-summary-text">
          <h2>{displayName}</h2>
          <p>Employee ID: {employeeId}</p>
          <p>{roleLabel}</p>
          <span className={`ctr-pi-active-pill ${profile?.isActive ? '' : 'inactive'}`}>
            <FaCheckCircle />
            {profile?.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
      {(uploadingPhoto || photoMessage || photoError) && (
        <div className={`ctr-pi-upload-status ${photoError ? 'error' : ''}`}>
          {uploadingPhoto ? 'Uploading photo...' : photoError || photoMessage}
        </div>
      )}

      <section className="ctr-pi-section-card">
        <h3>Basic Information</h3>
        <div className="ctr-pi-grid">
          {baseInfo.map((item) => (
            <label key={item.label} className={`ctr-pi-field ${item.label.includes('Address') ? 'full' : ''}`}>
              <span>{item.label}</span>
              <input readOnly value={item.value} />
            </label>
          ))}
        </div>
      </section>

      <section className="ctr-pi-section-card">
        <h3>Emergency Contact</h3>
        <div className="ctr-pi-grid">
          {emergencyInfo.map((item) => (
            <label key={item.label} className="ctr-pi-field">
              <span>{item.label}</span>
              <input readOnly value={item.value} />
            </label>
          ))}
        </div>
      </section>

      <section className="ctr-pi-section-card">
        <h3>Professional Details</h3>
        <div className="ctr-pi-grid">
          {professionalInfo.map((item) => (
            <label key={item.label} className="ctr-pi-field">
              <span>{item.label}</span>
              <input readOnly value={item.value} />
            </label>
          ))}
          <div className="ctr-pi-field full">
            <span>Skills</span>
            <div className="ctr-pi-skills">
              {skillTags.map((skill) => (
                <span key={skill} className="ctr-pi-chip">{skill}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ctr-pi-section-card">
        <h3>Financial Information</h3>
        <div className="ctr-pi-list">
          <div><FaDollarSign /> Payment Type: <strong>{toDisplay(profile?.payType)}</strong></div>
          <div><FaDollarSign /> Rate: <strong>{profile?.perHourRate !== null && profile?.perHourRate !== undefined ? `$${profile.perHourRate} / hour` : 'N/A'}</strong></div>
          <div><FaCalendarAlt /> Pay Cycle: <strong>{toDisplay(profile?.salaryTerm)}</strong></div>
          <div><FaCalendarAlt /> Payout Day: <strong>{toDisplay(profile?.paydayDay)}</strong></div>
          <div><FaCalendarAlt /> Joining Date: <strong>{toDisplay(profile?.joiningDate)}</strong></div>
        </div>
      </section>

      <section className="ctr-pi-section-card">
        <h3>Bank Details</h3>
        <div className="ctr-pi-list">
          <div><FaUniversity /> Bank Name: <strong>{toDisplay(profile?.fundName)}</strong></div>
          <div><FaUniversity /> BSB Number: <strong>{toDisplay(profile?.bsb)}</strong></div>
          <div><FaUniversity /> Account Number: <strong>{toDisplay(profile?.accountNumber)}</strong></div>
          <div><FaUniversity /> Account Holder Name: <strong>{toDisplay(profile?.accountName)}</strong></div>
        </div>
      </section>

      <section className="ctr-pi-section-card">
        <h3>Tax Information</h3>
        <div className="ctr-pi-list">
          <div><FaShieldAlt /> Tax File Number (TFN): <strong>{toDisplay(profile?.taxFileNumber)}</strong></div>
          <div><FaShieldAlt /> ABN Number: <strong>{toDisplay(profile?.abnNumber)}</strong></div>
          <div><FaShieldAlt /> Tax Residency Status: <strong>{toDisplay(profile?.taxResidency)}</strong></div>
          <div><FaShieldAlt /> Tax-Free Threshold Claim: <strong>{toDisplay(profile?.claimTaxFreeThreshold)}</strong></div>
        </div>
      </section>

      <section className="ctr-pi-section-card">
        <h3>Superannuation Details</h3>
        <div className="ctr-pi-list">
          <div><FaShieldAlt /> Fund Name: <strong>{toDisplay(profile?.fundName)}</strong></div>
          <div><FaShieldAlt /> SPIN Number: <strong>{toDisplay(profile?.spin)}</strong></div>
          <div><FaShieldAlt /> Fund ABN: <strong>{toDisplay(profile?.fundABN)}</strong></div>
          <div><FaShieldAlt /> Fund Contact Number: <strong>{toDisplay(profile?.fundTelephoneNumber)}</strong></div>
          <div><FaShieldAlt /> Member Number: <strong>{toDisplay(profile?.memberNumber)}</strong></div>
          <div><FaShieldAlt /> Superannuation Account Number: <strong>{toDisplay(profile?.superannuationAccountNumber)}</strong></div>
        </div>
      </section>

      <section className="ctr-pi-section-card">
        <div className="ctr-pi-docs-header">
          <h3>Uploaded Documents</h3>
          <button type="button" className="ctr-doc-manage-btn" onClick={openAddDocumentEditor}>
            <FaPlus /> Add Document
          </button>
        </div>
        {(docMessage || docError) && (
          <div className={`ctr-pi-upload-status ${docError ? 'error' : ''}`}>
            {docError || docMessage}
          </div>
        )}
        <div className="ctr-pi-docs">
          {resolvedUploadUrl ? (
            <div className="ctr-pi-doc-row">
              <span className="doc-left">
                <FaQuestionCircle />
                Profile Upload
              </span>
              <button type="button" onClick={() => openDocumentPreview(resolvedUploadUrl, 'Profile Upload')}>
                View
              </button>
            </div>
          ) : null}

          {workforceDocuments.map((doc) => {
            const fileUrl = resolveUploadUrl(doc?.fileUrl || doc?.fileKey);
            return (
              <div className="ctr-pi-doc-row" key={doc.id}>
                <span className="doc-left">
                  <FaQuestionCircle />
                  {doc?.documentType || 'Document'} - {doc?.fileName || 'File'}
                </span>
                <div className="ctr-doc-actions">
                  <button
                    type="button"
                    onClick={() => openDocumentPreview(fileUrl, doc?.fileName || doc?.documentType || 'Document')}
                    disabled={!fileUrl}
                  >
                    View
                  </button>
                  <button type="button" onClick={() => openEditDocumentEditor(doc)}>
                    <FaEdit /> Edit
                  </button>
                </div>
              </div>
            );
          })}

          {!resolvedUploadUrl && workforceDocuments.length === 0 ? (
            <div className="ctr-pi-doc-row">
              <span className="doc-left">
                <FaQuestionCircle />
                No documents uploaded
              </span>
            </div>
          ) : null}
        </div>
      </section>

      <section className="ctr-pi-section-card">
        <h3>Contact Information</h3>
        <div className="ctr-pi-list">
          <div><FaEnvelope /> Email: <strong>{toDisplay(profile?.email)}</strong></div>
          <div><FaPhoneAlt /> Phone: <strong>{toDisplay(profile?.contact)}</strong></div>
          <div><FaMapMarkerAlt /> Location: <strong>{toDisplay(profile?.address)}</strong></div>
        </div>
      </section>

      {loading && <div className="ctr-profile-loading">Loading profile...</div>}

      {documentEditor.open ? (
        <div className="ctr-doc-editor-overlay" onClick={resetDocumentEditor}>
          <div className="ctr-doc-editor-modal" onClick={(event) => event.stopPropagation()}>
            <div className="ctr-doc-editor-header">
              <h4>{documentEditor.mode === 'edit' ? 'Edit Document' : 'Add Document'}</h4>
              <button type="button" onClick={resetDocumentEditor}>X</button>
            </div>
            <div className="ctr-doc-editor-content">
              <label>
                Document Type
                <select value={docType} onChange={(event) => setDocType(event.target.value)}>
                  <option value="">Select document type</option>
                  {DOCUMENT_TYPES.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </label>
              {docType === 'Other' ? (
                <label>
                  Custom Document Type
                  <input
                    value={docTypeOther}
                    onChange={(event) => setDocTypeOther(event.target.value)}
                    placeholder="Enter document type"
                  />
                </label>
              ) : null}
              <label>
                Cleaning License
                <select value={docCleaningLicense} onChange={(event) => setDocCleaningLicense(event.target.value)}>
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </label>
              <label>
                Experience (Years)
                <input
                  type="number"
                  min="0"
                  max="60"
                  value={docExperience}
                  onChange={(event) => setDocExperience(event.target.value)}
                  placeholder="Enter years of experience"
                />
              </label>
              <div className="ctr-doc-file-row">
                <input
                  ref={docFileInputRef}
                  type="file"
                  className="ctr-pi-hidden-file-input"
                  onChange={handleDocumentFileChange}
                  accept="image/*,.pdf,.doc,.docx"
                />
                <button type="button" className="ctr-doc-manage-btn secondary" onClick={handlePickDocumentFile}>
                  {docFile ? 'Change File' : 'Choose File'}
                </button>
                <span className="ctr-doc-file-name">
                  {docFile?.name || documentEditor.fileName || 'No file selected'}
                </span>
              </div>
              {docError ? <p className="ctr-doc-error">{docError}</p> : null}
            </div>
            <div className="ctr-doc-editor-footer">
              <button type="button" className="ctr-doc-manage-btn secondary" onClick={resetDocumentEditor}>
                Cancel
              </button>
              <button type="button" className="ctr-doc-manage-btn" onClick={saveWorkforceDocument} disabled={savingDocument}>
                {savingDocument ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {documentPreview ? (
        <div className="ctr-doc-preview-overlay" onClick={closeDocumentPreview}>
          <div className="ctr-doc-preview-modal" onClick={(event) => event.stopPropagation()}>
            <div className="ctr-doc-preview-header">
              <h4>{documentPreview.title}</h4>
              <button type="button" onClick={closeDocumentPreview}>X</button>
            </div>
            <div className="ctr-doc-preview-content">
              {documentPreview.isImage ? (
                <img src={documentPreview.url} alt={documentPreview.title} className="ctr-doc-preview-image" />
              ) : documentPreview.isPdf ? (
                <iframe
                  src={documentPreview.url}
                  title={documentPreview.title}
                  className="ctr-doc-preview-frame"
                />
              ) : (
                <div className="ctr-doc-preview-fallback">
                  <p>This file type cannot be previewed here.</p>
                  <a href={documentPreview.url} target="_blank" rel="noreferrer">
                    Open file
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

